import type { AuthFailureKind } from '@common/bt-auth/auth-failure';
import { shouldRedirectToLoginOnAuthFailure } from '@common/bt-auth/auth-failure';

/**
 * The protected-route (auth === true) decision, extracted as a PURE function.
 *
 * This is the highest-blast-radius branch in the app: get it wrong in one direction and
 * every user is locked out, in the other a rejected session keeps rendering. Pulling it
 * out of the component makes it directly unit-testable in the node jest env (no DOM), and
 * keeps the ordering of the branches — which is load-bearing — in one readable place.
 *
 * It decides only; it has no access to storage, dispatch or navigation, so no decision path
 * can clear the stored token. That is a property the tests pin.
 */

export interface ProtectedRouteInputs {
  /** A token exists in localStorage / cookie / URL / the extension hub. */
  hasToken: boolean;
  /** redux `UserReducer.userId` is populated. */
  hasUserId: boolean;
  /** the hydrated user object is populated. */
  hasUser: boolean;
  isBusy: boolean;
  isSuccess: boolean;
  isFail: boolean;
  /** why the last attempt failed; only meaningful when `isFail`. */
  failureKind?: AuthFailureKind;
}

export type ProtectedRouteDecision =
  /** Send the visitor to /login. Never clears the token. */
  | { type: 'redirect-login' }
  /** Show a loader; auth is still resolving. */
  | { type: 'loader'; message: 'authenticating' | 'finalizing' }
  /**
   * The backend could not be reached / did not judge the credential, and we still hold a
   * token. Hold the session, retry in the background, and tell the user the SERVER is the
   * problem. This is NOT a logout.
   */
  | { type: 'backend-unreachable' }
  /** Fall through to the role gate and render the route. */
  | { type: 'render' };

export const decideProtectedRoute = ({
  hasToken,
  hasUserId,
  hasUser,
  isBusy,
  isSuccess,
  isFail,
  failureKind,
}: ProtectedRouteInputs): ProtectedRouteDecision => {
  // Once we have BOTH a userId and a user object the session is valid; keep rendering even if
  // a background re-auth (e.g. a bt-extension-login re-fire, or a re-auth that failed against
  // a stalled backend) flips the status to busy/fail — it must never blank out an
  // authenticated app.
  const isAuthenticated = hasUserId && hasUser;

  // No token, no user, not authenticated → straight to login. Nothing to preserve.
  if (!hasToken && !hasUserId && !isSuccess) return { type: 'redirect-login' };

  // Auth in flight → loader (unless already authenticated, where a background re-auth must
  // not replace the rendered session).
  if (isBusy && !isAuthenticated) return { type: 'loader', message: 'authenticating' };

  // Definitive failure with no user. A stored token kept us out of the redirect branch above,
  // so without a decision here we would hang on the finalizing loader forever.
  //
  // The failure REASON decides. Previously every failure bounced to /login with the note that
  // "a transient blip self-recovers" — but from the user's seat the blip is what CAUSED the
  // bounce: a slow/restarting backend produced a network error or timeout, and a perfectly
  // valid session was thrown at the login page mid-work. So:
  //   - unreachable + a stored token → HOLD the session, retry in the background.
  //   - anything else (401/403 rejection, no credential, unclassified) → bounce, as before.
  // The token is not cleared in either case; only an explicit logout clears it.
  if (isFail && !hasUserId) {
    if (hasToken && !shouldRedirectToLoginOnAuthFailure(failureKind)) {
      return { type: 'backend-unreachable' };
    }
    return { type: 'redirect-login' };
  }

  // Success status but user not yet hydrated in this context (page-world vs content-script
  // race) → brief finalizing loader. An authenticated user falls through to render.
  if (!isAuthenticated && (!isSuccess || !hasUserId)) return { type: 'loader', message: 'finalizing' };

  return { type: 'render' };
};
